import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Register the PWA service worker in production builds only. Dev mode skips it
// to keep hot-reload simple — service worker caching can mask code changes.
//
// On detecting a new worker waiting to take over, we ping it with SKIP_WAITING
// and reload exactly once when control transfers. This makes new deploys land
// for already-open tabs without the user having to refresh — and prevents the
// old "stale bundle after deploy" class of bugs (e.g. the Compose newsletter
// inputs that rendered un-typeable because they came from a previous build).
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").then((reg) => {
      const promote = (worker) => {
        if (!worker) return;
        worker.addEventListener("statechange", () => {
          if (worker.state === "installed" && navigator.serviceWorker.controller) {
            worker.postMessage("SKIP_WAITING");
          }
        });
      };
      promote(reg.installing);
      reg.addEventListener("updatefound", () => promote(reg.installing));
      // Catch a worker that is already waiting from a prior load.
      if (reg.waiting && navigator.serviceWorker.controller) {
        reg.waiting.postMessage("SKIP_WAITING");
      }
    }).catch(() => { /* harmless — offline / unsupported */ });
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
